import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Keeps engraving lines paired with their watch in the cart.
 *
 * Each engraving line carries `_engraving_line` + `_engraving_id`; its watch
 * carries the matching `_engraving_id`. When a watch is removed, its engraving
 * line is orphaned — this removes it so the shopper never pays for an engraving
 * with no watch. (The reverse — removing the engraving — needs no code: the text
 * lives on the engraving line, so it simply disappears, and the watch keeps only
 * a harmless hidden id.)
 *
 * Loaded from the cart snippet, so it runs wherever the cart renders (drawer on
 * any page + the cart page).
 */

const root = () => window.Shopify?.routes?.root || '/';
let busy = false;
let timer = null;

async function reconcile() {
  if (busy) return;
  let cart;
  try {
    cart = await (await fetch(`${root()}cart.js`)).json();
  } catch {
    return;
  }
  const items = cart.items || [];

  // Link ids still backed by a watch (a line with the id but not the engraving line).
  const watchIds = new Set();
  for (const item of items) {
    const p = item.properties || {};
    if (p._engraving_id && !p._engraving_line) watchIds.add(String(p._engraving_id));
  }

  const orphans = items.filter((item) => {
    const p = item.properties || {};
    return p._engraving_line && !watchIds.has(String(p._engraving_id));
  });
  if (orphans.length === 0) return;

  busy = true;
  try {
    for (const orphan of orphans) {
      await fetch(`${root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: orphan.key, quantity: 0 }),
      });
    }
    const updated = await (await fetch(`${root()}cart.js`)).json();
    document.dispatchEvent(
      new CartUpdateEvent(updated, 'engraving-reconcile', { source: 'engraving-reconcile' })
    );
  } catch (error) {
    console.error('[engraving-cart] reconcile failed:', error);
  } finally {
    busy = false;
  }
}

if (!window.__engravingCartInit) {
  window.__engravingCartInit = true;
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.data?.source === 'engraving-reconcile') return; // don't react to ourselves
    clearTimeout(timer);
    timer = setTimeout(reconcile, 150);
  });
  // Cart page may load with an already-orphaned engraving.
  reconcile();
}
